import {
  MessarioLaunchResolveError,
  MessarioLaunchResolveSuccess
} from '@lib/messarioLaunch/types';

export class MessarioLaunchResolveFailure extends Error {
  constructor(
    public readonly kind: 'configuration' | 'network' | 'rejected' | 'invalid-response',
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = 'MessarioLaunchResolveFailure';
  }
}

const STATUS_MESSAGES: Partial<Record<number, string>> = {
  403: 'This launch link is not allowed from this Web K origin.',
  409: 'This launch link could not be resolved because its state changed.',
  410: 'This launch link has expired.',
  422: 'This launch link is invalid or its Messario account is unavailable.',
  503: 'Messario account launch is temporarily unavailable.'
};

const ERROR_MESSAGES: Partial<Record<string, string>> = {
  account_stale: 'This Messario account is no longer available for launch.',
  token_replayed: 'This launch link has already been used.',
  resolve_in_progress: 'This launch link is already being resolved. Try opening it again shortly.'
};

export async function resolveMessarioLaunch(
  token: string,
  panelOrigin: string,
  fetchValue: typeof fetch = fetch
): Promise<MessarioLaunchResolveSuccess> {
  if(!panelOrigin) {
    throw new MessarioLaunchResolveFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOrigin.replace(/\/+$/, '')}/api/telegram-web/launch/resolve`,
      {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({token})
      }
    );
  } catch(err) {
    throw new MessarioLaunchResolveFailure(
      'network',
      'Could not contact Messario Panel. Check the Panel and browser connection.'
    );
  }

  let body: MessarioLaunchResolveSuccess | MessarioLaunchResolveError;
  try {
    body = await response.json();
  } catch(err) {
    throw new MessarioLaunchResolveFailure(
      'invalid-response',
      'Messario Panel returned an unreadable response.',
      response.status
    );
  }

  if(!response.ok) {
    const serverError = 'error' in body ? body.error : undefined;
    throw new MessarioLaunchResolveFailure(
      'rejected',
      ERROR_MESSAGES[serverError] ||
        STATUS_MESSAGES[response.status] ||
        serverError ||
        'Messario rejected this launch link.',
      response.status
    );
  }

  if(
    !('account' in body) ||
    !body.account ||
    typeof body.account.panel_user_id !== 'string' ||
    typeof body.account.account_id !== 'string' ||
    typeof body.account.external_user_id !== 'string' ||
    !body.account.external_user_id
  ) {
    throw new MessarioLaunchResolveFailure(
      'invalid-response',
      'Messario Panel returned an incomplete account response.',
      response.status
    );
  }

  if('open_peer' in body && body.open_peer != null) {
    if(typeof body.open_peer !== 'string' || !body.open_peer.trim()) {
      throw new MessarioLaunchResolveFailure(
        'invalid-response',
        'Messario Panel returned an incomplete open_peer response.',
        response.status
      );
    }
  }

  if('templates_access' in body && body.templates_access != null) {
    const access = body.templates_access;
    if(
      typeof access !== 'object' ||
      typeof access.token !== 'string' ||
      !access.token ||
      typeof access.exp !== 'number'
    ) {
      throw new MessarioLaunchResolveFailure(
        'invalid-response',
        'Messario Panel returned an incomplete templates access response.',
        response.status
      );
    }
  }

  if('ai_bundle' in body && body.ai_bundle != null) {
    const bundle = body.ai_bundle;
    if(typeof bundle !== 'object' || typeof (bundle as {enabled?: unknown}).enabled !== 'boolean') {
      throw new MessarioLaunchResolveFailure(
        'invalid-response',
        'Messario Panel returned an incomplete AI bundle response.',
        response.status
      );
    }

    if((bundle as {enabled: boolean}).enabled) {
      const enabled = bundle as {
        enabled: true,
        prompt_revision?: unknown,
        compiled_at?: unknown,
        model?: unknown,
        history_limit?: unknown,
        folder_id?: unknown,
        features?: {one_shot_instruction?: unknown, draft_only?: unknown}
      };
      if(
        typeof enabled.prompt_revision !== 'number' ||
        (enabled.compiled_at !== null && typeof enabled.compiled_at !== 'string') ||
        typeof enabled.model !== 'string' ||
        typeof enabled.history_limit !== 'number' ||
        (enabled.folder_id !== null && typeof enabled.folder_id !== 'number') ||
        !enabled.features ||
        typeof enabled.features.one_shot_instruction !== 'boolean' ||
        typeof enabled.features.draft_only !== 'boolean'
      ) {
        throw new MessarioLaunchResolveFailure(
          'invalid-response',
          'Messario Panel returned an incomplete AI bundle response.',
          response.status
        );
      }
    }
  }

  return body;
}
